import React, { useState, useEffect } from 'react'
import { Analysis, AnalysisSummary as AnalysisItem, fetchAnalysisDetail, updateAnalysisReview } from '../../../services/api'
import { CRIT, STATUS, IMPACT, OWNER, TAG_LABEL, daysUntil, fmtDate } from './helpers'
import { Lozenge, SmallTag, CountryChip } from './atoms'
import { ReviewModal } from './ReviewModal'
import { AnalysisSkeleton } from './Skeleton'

export function DetailPage({
  id, items, setItems, onBack, animDir, userEmail, teamName, onNeedEmail,
}: {
  id: number
  items: AnalysisItem[]
  setItems: React.Dispatch<React.SetStateAction<AnalysisItem[]>>
  onBack: () => void
  animDir: 'forward' | 'back'
  userEmail: string | null
  teamName?: string | null
  onNeedEmail: () => void
}) {
  const summary = items.find(i => i.id === id)
  const [detail, setDetail] = useState<Analysis | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [tab, setTab] = useState<'impact' | 'provisions' | 'ideas'>('impact')
  const [infoOpen, setInfoOpen] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [dismissing, setDismissing] = useState(false)

  useEffect(() => {
    setTab('impact')
    setInfoOpen(false)
    setReviewOpen(false)
    setDetail(null)
    setLoadError(null)
    fetchAnalysisDetail(id).then(setDetail).catch(err => setLoadError(err.message))
  }, [id])

  if (!summary) return null
  if (loadError) return <p className="text-[13px] text-an-red px-8 pt-7">Failed to load: {loadError}</p>
  if (!detail) {
    return (
      <div className="flex-1 overflow-y-auto bg-an-bg font-geist">
        <div className="max-w-[860px] mx-auto px-6 pt-7 pb-16 flex flex-col gap-5"><AnalysisSkeleton /></div>
      </div>
    )
  }
  // The list holds the per-user/team review status, and review changes update
  // it, so it wins over whatever the detail endpoint returned.
  const item: Analysis = { ...detail, review_status: summary.review_status, reviewed_by: summary.reviewed_by }

  const crit = CRIT[item.criticality] ?? CRIT.medium
  const stat = STATUS[item.review_status] ?? STATUS.pending
  const days = daysUntil(item.compliance_deadline)
  const overdue = days < 0
  const urgent = days >= 0 && days <= 90
  const near = days > 90 && days <= 180
  const deadlineColor = overdue ? 'var(--an-red)' : urgent ? 'var(--an-amber)' : near ? 'var(--an-yellow)' : 'var(--an-ink-2)'
  const deadlineBg = overdue ? 'var(--an-red-bg)' : urgent ? 'var(--an-amber-bg)' : near ? 'var(--an-yellow-bg)' : 'var(--an-surface-2)'
  const deadlineBorder = overdue ? 'var(--an-red-mid)' : urgent ? 'var(--an-amber-mid)' : near ? 'var(--an-yellow-mid)' : 'var(--an-line)'
  const anim = animDir === 'forward' ? 'animate-[an-slideInRight_0.22s_ease]' : 'animate-[an-slideInLeft_0.22s_ease]'

  const tabs = [
    { id: 'impact' as const, label: 'Impact' },
    { id: 'provisions' as const, label: 'Provisions' },
    { id: 'ideas' as const, label: 'Ideas' },
  ]

  return (
    <div className={`flex-1 overflow-y-auto bg-an-bg font-geist ${anim}`}>
      <div className="max-w-[1100px] mx-auto px-8 pt-7 pb-20">

        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 mb-6 bg-transparent border-none cursor-pointer text-an-ink-3 hover:text-an-ink text-[13px] p-0 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          Analysis
        </button>

        <div className="grid grid-cols-[260px_1fr] gap-8 items-start">

          {/* sidebar */}
          <div className="sticky top-5 flex flex-col gap-2.5">
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <CountryChip code={item.country} sm />
                <span className="text-[10px] text-an-ink-3 italic tracking-[0.01em]">
                  {[item.celex_number, item.legislation_type].filter(Boolean).join(' · ')}
                </span>
              </div>
              <div className="text-xl font-[750] text-an-ink leading-tight tracking-[-0.035em]">{item.title}</div>
              {item.original_title && item.original_title !== item.title && (
                <div className="text-[11.5px] text-an-ink-3 italic mt-[5px] leading-snug">
                  Original: {item.original_title}
                </div>
              )}
            </div>

            <div className="flex gap-[5px] flex-wrap">
              <Lozenge c={crit.c} bg={crit.bg}>{crit.label}</Lozenge>
              <Lozenge c={stat.c} bg={stat.bg}>{stat.label}</Lozenge>
              {item.requires_action && <Lozenge c="var(--an-red)" bg="var(--an-red-bg)">Action required</Lozenge>}
              {item.flagged_for_review && <span className="text-[13px] text-an-amber self-center">{'⚑'}</span>}
            </div>

            {item.compliance_deadline && (
              <div
                className="rounded-[9px] px-3.5 py-3 flex items-center justify-between gap-2.5"
                style={{ background: deadlineBg, border: `1px solid ${deadlineBorder}` }}
              >
                <div>
                  <div className="text-[9px] font-bold tracking-[0.09em] uppercase mb-1 opacity-70" style={{ color: deadlineColor }}>Deadline</div>
                  <div className="flex items-center gap-1.5">
                    <div className="text-sm font-bold font-mono tracking-[-0.02em] leading-none" style={{ color: deadlineColor }}>{fmtDate(item.compliance_deadline)}</div>
                    {item.deadline_notes && (
                      <div className="relative inline-flex group">
                        <span
                          className="w-3.5 h-3.5 rounded-full border border-current opacity-55 text-[9px] font-bold flex items-center justify-center cursor-default italic shrink-0"
                          style={{ color: deadlineColor, fontFamily: 'Georgia, serif' }}
                        >i</span>
                        <div className="absolute left-5 top-1/2 -translate-y-1/2 w-[200px] bg-an-surface-3 border border-an-line-mid rounded-[7px] px-2.5 py-2 text-[11px] text-an-ink-2 leading-normal z-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          {item.deadline_notes}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {overdue
                    ? <div className="text-xs font-bold rounded-[5px] px-2 py-[3px]" style={{ color: deadlineColor, background: 'var(--an-red-mid)' }}>Overdue</div>
                    : <div>
                        <div className="text-2xl font-extrabold leading-none font-mono" style={{ color: deadlineColor }}>{days}</div>
                        <div className="text-[9px] font-medium mt-0.5 opacity-80" style={{ color: deadlineColor }}>days left</div>
                      </div>
                  }
                </div>
              </div>
            )}

            <div className="px-3 py-[9px] bg-an-surface rounded-lg border border-an-line flex flex-col gap-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-an-ink-3">Owner</span>
                <span className="text-an-ink font-semibold">{OWNER[item.action_owner] ?? item.action_owner}</span>
              </div>
              <div className="h-px bg-an-line" />
              <div className="flex justify-between text-xs">
                <span className="text-an-ink-3">Confidence</span>
                <span className="font-semibold capitalize" style={{ color: CRIT[item.relevance_confidence]?.c ?? 'var(--an-ink-2)' }}>{item.relevance_confidence}</span>
              </div>
              <div className="h-px bg-an-line" />
              <div className="flex justify-between text-xs">
                <span className="text-an-ink-3">Mandatory</span>
                <span className="text-an-ink font-semibold">{item.is_mandatory ? 'Yes' : 'No'}</span>
              </div>
            </div>

            {item.requires_action && (
              <div className="bg-an-red-bg border border-an-red-mid rounded-lg px-[13px] py-[11px]">
                <div className="text-[9px] font-bold text-an-red tracking-[0.07em] uppercase mb-[5px]">
                  {OWNER[item.action_owner] ?? item.action_owner} &mdash; Action required
                </div>
                <p className="text-xs text-an-ink-2 leading-relaxed m-0">{item.action_description}</p>
              </div>
            )}

            <div className="flex gap-1 flex-wrap">
              {item.relevant_to.map(t => <SmallTag key={t}>{TAG_LABEL[t] || t}</SmallTag>)}
            </div>

            <button
              onClick={() => { if (userEmail) setReviewOpen(true); else { onNeedEmail(); onBack() } }}
              className="mt-1 py-[9px] px-3.5 rounded-lg border border-an-line-mid bg-an-surface text-an-ink hover:border-an-accent hover:text-an-accent text-[13px] font-semibold cursor-pointer transition-all flex items-center justify-center gap-[7px]"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 6h10M6 1v10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
              Add Review
            </button>

            <button
              onClick={async () => {
                if (!userEmail) { onNeedEmail(); onBack(); return }
                setDismissing(true)
                try {
                  const updated = await updateAnalysisReview(id, 'dismissed', userEmail, teamName || undefined)
                  setItems(prev => prev.map(i => i.id === id ? { ...i, review_status: updated.review_status, reviewed_by: updated.reviewed_by } : i))
                  onBack()
                } catch { setDismissing(false) }
              }}
              disabled={dismissing}
              className="mt-0.5 py-[7px] px-3.5 rounded-lg border border-transparent bg-transparent text-an-ink-3 hover:border-an-ink-3 text-xs font-medium cursor-pointer transition-all flex items-center justify-center gap-1.5"
              style={{ opacity: dismissing ? 0.5 : 1 }}
            >
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>
              {dismissing ? 'Dismissing…' : 'Dismiss'}
            </button>
          </div>

          {/* main content */}
          <div>
            <p className="text-sm text-an-ink-2 leading-[1.75] mb-6 border-b border-an-line pb-5">{item.summary}</p>

            <div className="flex border-b border-an-line mb-5">
              {tabs.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-[18px] py-[9px] text-[13px] bg-transparent cursor-pointer transition-colors whitespace-nowrap -mb-px ${
                    tab === t.id ? 'font-semibold text-an-accent' : 'font-normal text-an-ink-3'
                  }`}
                  style={{ border: 'none', borderBottom: `2px solid ${tab === t.id ? 'var(--an-accent)' : 'transparent'}` }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'impact' && (
              <div className="flex flex-col gap-3.5">
                <div className="flex items-center gap-[7px]">
                  <div className="text-[10px] font-bold text-an-ink-3 tracking-[0.09em] uppercase">Module Impact</div>
                  <button
                    onClick={() => setInfoOpen(o => !o)}
                    title="Workday context"
                    className={`w-[17px] h-[17px] rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 transition-all p-0 italic cursor-pointer ${
                      infoOpen
                        ? 'border border-an-accent bg-an-accent-dim text-an-accent'
                        : 'border border-an-line-mid bg-transparent text-an-ink-3'
                    }`}
                    style={{ fontFamily: 'Georgia, serif' }}
                  >i</button>
                </div>
                {infoOpen && (
                  <div className="px-3.5 py-[11px] bg-an-surface rounded-lg border border-an-line animate-[an-in_0.12s_ease]">
                    <p className="text-[13px] text-an-ink-2 leading-relaxed m-0">{item.workday_overall_impact}</p>
                  </div>
                )}
                <div className="rounded-lg overflow-hidden border border-an-line">
                  {item.workday_modules.map((m, i) => {
                    const imp = IMPACT[m.impact_type] ?? IMPACT.potential
                    return (
                      <div
                        key={m.module}
                        className={`grid grid-cols-[130px_80px_1fr] px-3.5 py-[11px] items-start ${
                          i % 2 === 0 ? 'bg-an-surface' : 'bg-an-surface-2'
                        } ${i < item.workday_modules.length - 1 ? 'border-b border-an-line' : ''}`}
                      >
                        <span className="text-[13px] font-semibold text-an-ink">{m.module}</span>
                        <Lozenge c={imp.c} bg={imp.bg} sm>{imp.label}</Lozenge>
                        <span className="text-[12.5px] text-an-ink-2 leading-relaxed pl-2.5">{m.impact_description}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {tab === 'provisions' && (
              <div className="flex flex-col gap-2">
                <div className="text-[10px] font-bold text-an-ink-3 tracking-[0.09em] uppercase mb-0.5">Key Provisions</div>
                {item.key_provisions.map((p, i) => (
                  <div key={i} className="flex gap-3 text-[13.5px] text-an-ink-2 leading-relaxed px-3.5 py-[11px] bg-an-surface rounded-lg border border-an-line">
                    <span className="text-an-accent shrink-0 text-base leading-none mt-0.5">{'›'}</span>{p}
                  </div>
                ))}
                <div className="mt-1.5 px-3.5 py-3 bg-an-surface-2 rounded-lg border border-an-line">
                  <div className="text-[9px] font-bold text-an-ink-3 tracking-[0.09em] uppercase mb-1.5">Relevance Rationale</div>
                  <p className="text-[13px] text-an-ink-2 leading-relaxed m-0">{item.relevance_reasoning}</p>
                </div>
              </div>
            )}

            {tab === 'ideas' && (
              <div className="flex flex-col gap-2.5">
                <div className="text-[10px] font-bold text-an-ink-3 tracking-[0.09em] uppercase mb-0.5">Response Ideas</div>
                {item.response_ideas.map((idea, i) => (
                  <div key={i} className="px-4 py-[15px] bg-an-surface rounded-[9px] border border-an-line">
                    <p className="text-[13px] text-an-ink-2 leading-relaxed m-0">{idea}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {reviewOpen && userEmail && (
        <ReviewModal
          item={item}
          userEmail={userEmail}
          teamName={teamName}
          onSave={(newStatus, reviewedBy) => setItems(prev => prev.map(i => i.id === id ? { ...i, review_status: newStatus, reviewed_by: reviewedBy ?? i.reviewed_by } : i))}
          onClose={() => setReviewOpen(false)}
        />
      )}

    </div>
  )
}
